/**
 * Preferência de menu recolhido/expandido, por usuário e por organização.
 *
 * Não existe hoje um endpoint genérico de "preferências de UI" — só
 * `PATCH /auth/me/preferencias`, que grava idioma e fuso (ver
 * `PreferenciasPage.tsx`). Criar um endpoint e uma tabela só para lembrar se a
 * pessoa prefere o menu recolhido seria desproporcional: a especificação da
 * reforma visual permite explicitamente usar armazenamento local com chave
 * versionada e escopo de tenant/usuário quando não houver mecanismo pronto.
 *
 * A chave inclui `organizationId` E `userId`: a mesma pessoa pode ter a
 * preferência diferente em cada empresa que administra (CERES-008), e o mesmo
 * navegador pode servir mais de uma conta ao longo do tempo — sem o escopo por
 * usuário, a preferência de quem saiu vazaria para quem loga em seguida no
 * mesmo aparelho.
 *
 * PADRÃO: recolhido. A especificação da reforma pede menu recolhido por
 * padrão no desktop para quem ainda não tem preferência salva — quem já
 * escolheu expandir continua vendo o que escolheu.
 */

import { useCallback, useEffect, useState } from 'react';

const VERSAO_DA_CHAVE = 'v1';

function chaveDoMenu(organizationId: string, userId: string): string {
  return `crm.menu.recolhido.${VERSAO_DA_CHAVE}:${organizationId}:${userId}`;
}

function lerPreferencia(chave: string): boolean | null {
  try {
    const bruto = window.localStorage.getItem(chave);
    if (bruto === null) return null;
    return bruto === '1';
  } catch {
    // Navegador privado, cota esgotada, ou localStorage bloqueado: sem
    // preferência lembrada, mas a aplicação segue funcionando com o padrão.
    return null;
  }
}

function gravarPreferencia(chave: string, recolhido: boolean): void {
  try {
    window.localStorage.setItem(chave, recolhido ? '1' : '0');
  } catch {
    // Mesma tolerância da leitura: não persistir não é motivo para quebrar a
    // troca de estado em memória.
  }
}

export function useMenuColapsado(
  organizationId: string,
  userId: string,
): { colapsado: boolean; alternar: () => void; definir: (valor: boolean) => void } {
  const chave = chaveDoMenu(organizationId, userId);
  const [colapsado, setColapsado] = useState<boolean>(() => lerPreferencia(chave) ?? true);

  // Troca de organização/usuário (CERES-008, mesma aba): recarrega a
  // preferência daquele escopo em vez de continuar com a do escopo anterior.
  useEffect(() => {
    setColapsado(lerPreferencia(chave) ?? true);
  }, [chave]);

  const definir = useCallback(
    (valor: boolean): void => {
      setColapsado(valor);
      gravarPreferencia(chave, valor);
    },
    [chave],
  );

  const alternar = useCallback((): void => {
    setColapsado((atual) => {
      const proximo = !atual;
      gravarPreferencia(chave, proximo);
      return proximo;
    });
  }, [chave]);

  return { colapsado, alternar, definir };
}
